import { useState, useEffect, useCallback } from 'react';
import API from '../utils/API';
import methods from '../utils/common-methods';
import * as modalUtils from '../utils/modalUtils';

/**
 * Custom hook for modal initialization with site selection functionality
 * @param {Object} options - Configuration options
 * @param {string} options.modalId - ID of the modal element
 * @param {Function} options.siteExtractor - Function to extract site information from user data
 * @returns {Object} - State and handlers for the modal
 */
const useModalInitializationForSites = ({
  modalId,
  multiSelect = false,
  //siteExtractor = modalUtils.extractStringBasedSiteInfo,
}) => {
  // State sites
  const [sites, setSites] = useState([]);
  const [selectedSiteIds, setSiteIds] = useState([]);
  const [cms, setCms] = useState("");
  // Error states
  const [siteDropdownError, setSiteDropdownError] = useState('');


  /**
   * Sort site tree by name in ascending alphabetical order
   * @param {Array} siteTree - The site tree to sort
   * @returns {Array} - Sorted site tree
   */
  const sortSiteTreeByName = useCallback((siteTree) => {
    if (!siteTree || !Array.isArray(siteTree) || siteTree.length === 0) {
      return [];
    }
    
    // Create a copy of the site tree to avoid modifying the original
    const sortedTree = [...siteTree];
    
    // Sort the current level by name (ascending)
    sortedTree.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    // Recursively sort children
    sortedTree.forEach(site => {
      if (site.children && site.children.length > 0) {
        site.children = sortSiteTreeByName(site.children);
      }
    });
    
    return sortedTree;
  }, []);

  //Load sites when sites is empty
  useEffect(() => {
    if (sites.length === 0) {
      API.getSignagePlusSites()
        .then((res) => {
          if(res.status === 200 && res.data && res.data.length > 0){
            console.log('***MEI********sites.data', res.data)
            let siteTree = methods.buildSignageSitesTree(res.data);
            setSites(siteTree);
            setCms(res.data[0].cmsName || '');
          }
        });
    }
  }, [sites]);

  // Listen for modal show event to initialize selections
  useEffect(() => {
    const modal = document.getElementById(modalId);
    
    if (modal) {
      // Handle modal show event
      const handleModalShow = () => {
        // Reset state when modal is shown
        setSiteIds([]);
        
        // Clear any previous errors
        setSiteDropdownError('');        
      };
      
      // Handle modal hidden event (fires after modal is completely hidden)
      const handleModalHidden = () => {
        setSiteDropdownError('');
      };
      
      // Add event listeners for modal show and hidden events
      modal.addEventListener('show.bs.modal', handleModalShow);
      modal.addEventListener('hidden.bs.modal', handleModalHidden);
      
      // Clean up event listeners
      return () => {
        modal.removeEventListener('show.bs.modal', handleModalShow);
        modal.removeEventListener('hidden.bs.modal', handleModalHidden);
      };
    }
  }, [ modalId, sortSiteTreeByName]);

  // Tree manipulation functions
  function flattenSiteTree(siteTree, sites = [], tier = 0, parent_checked) {
    siteTree.forEach((site) => {
      if (selectedSiteIds.includes(site.id) || parent_checked) {
        sites.push(generateSiteItem(site, tier, true));
        if (site.children && site.children.length > 0) {
          flattenSiteTree(site.children, sites, tier + 1, true);
        }
      } else {
        sites.push(generateSiteItem(site, tier, false));
        if (site.children && site.children.length > 0) {
          flattenSiteTree(site.children, sites, tier + 1, false);
        }
      }
    });
    return sites;
  }

  function getChildrenIds(siteTree, childrenIds = []) {
    siteTree.forEach((site) => {
      childrenIds.push(site.id);
      if (site.children && site.children.length > 0) {
        getChildrenIds(site.children, childrenIds);
      }
    });
    return childrenIds;
  }

  function findSiteInTree(siteTree, siteId) {
    for (let i = 0; i < siteTree.length; i++) {
      const site = siteTree[i];
      if (site.id === siteId) {
        return site;
      } else if (site.children && site.children.length > 0) {
        const childSite = findSiteInTree(site.children, siteId);
        if (childSite) return childSite;
      }
    }
    return null;
  }
  


  function generateSiteItem(site, tier, parent_checked) {
    // Check if all children of this site are selected
    const areAllChildrenSelected = () => {
      if (!site.children || site.children.length === 0) {
        return false;
      }
      
      const childrenIds = getChildrenIds(site.children);
      return childrenIds.length > 0 && childrenIds.every(id => selectedSiteIds.includes(id));
    };
    
    // A site should be disabled if its parent is checked
    let isDisabled = parent_checked && !selectedSiteIds.includes(site.id);
    if(!multiSelect && !isDisabled && selectedSiteIds.length > 0 && !selectedSiteIds.includes(site.id)){
      isDisabled = true; // Disable if not selected and not a parent
    }
    
    // Handle click on the entire list item
    const handleItemClick = (e) => {
      if (!isDisabled) {
        e.preventDefault();
        e.stopPropagation();
        handleSiteClick(site.id, e);
      } else {
        // Prevent dropdown from closing when clicking disabled items
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    // Determine if checkbox should appear checked
    // A site is checked if it's explicitly selected OR if its parent is checked OR if all its children are selected
    const isChecked = selectedSiteIds.includes(site.id) || parent_checked || areAllChildrenSelected();
    
    return (
      <li 
        key={site.id + site.cmsId} 
        value={site.id} 
        onClick={handleItemClick}
        style={{ display: 'block', width: '100%' }}
      >
        <span 
          className='dropdown-item' 
          style={{
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            paddingLeft: (tier * 8) + 16, // Add padding inside the span instead of the li
            display: 'block',
            width: '100%'
          }}
        >
        <input
            key={`checkbox-${site.id}`} 
            type="checkbox"
            style={{ marginRight: 8 }} 
            disabled={isDisabled} 
            checked={isChecked} 
            readOnly 
            onClick={(e) => {
              // Prevent the event from bubbling up to the li's onClick
              e.stopPropagation();
              if (!isDisabled) {
                handleSiteClick(site.id, e);
              }
            }} 
          />
          {site.name}
        </span>
      </li>
    );
  }

  function handleSiteClick(siteId, e) {
    e.stopPropagation();
    let updatedSiteIds = [...selectedSiteIds];
    
    if (selectedSiteIds.includes(siteId)) {
      // Remove the site ID if it's already selected
      updatedSiteIds = updatedSiteIds.filter(id => id !== siteId);
      
      // If this is a parent site, we need to check if we should select all children
      // when deselecting the parent
      const siteObj = findSiteInTree(sites, siteId);
      if (siteObj && siteObj.children && siteObj.children.length > 0) {
        // When deselecting a parent, we don't automatically select all children
        // This is the expected behavior based on the requirements
      }
    } else {
      // Add the site ID if it's not already selected
      updatedSiteIds.push(siteId);
      
      // Handle children sites - when selecting a parent, remove all children from selection
      const siteObj = findSiteInTree(sites, siteId);
      if (siteObj && siteObj.children && siteObj.children.length > 0) {
        const childrenIds = getChildrenIds(siteObj.children);
        // Remove all children IDs from selection
        updatedSiteIds = updatedSiteIds.filter(id => !childrenIds.includes(id));
      }
    }
    
    // Use optimizeSelectedSites to handle parent-child relationships consistently
    // This will ensure that when all children of a parent are selected, the parent is selected too
    console.log('Site IDs before optimization:', updatedSiteIds);
    const optimizedIds = optimizeSelectedSites(updatedSiteIds, sites);
    console.log('Site IDs after optimization:', optimizedIds);
    
    setSiteIds(optimizedIds);
  }

  // Helper function to optimize site selection by handling parent-child relationships
  function optimizeSelectedSites(siteIds, siteTree) {
    if (!siteIds || !Array.isArray(siteIds) || siteIds.length === 0) {
      return [];
    }
    
    // Build a map of parent-child relationships
    const parentChildMap = {};
    const childParentMap = {};
    
    // Function to build the parent-child relationship maps
    function buildRelationshipMaps(tree, parentId = null) {
      tree.forEach(site => {
        if (parentId) {
          // Add to parent->children map
          if (!parentChildMap[parentId]) {
            parentChildMap[parentId] = [];
          }
          parentChildMap[parentId].push(site.id);
          
          // Add to child->parent map
          childParentMap[site.id] = parentId;
        }
        
        if (site.children && site.children.length > 0) {
          buildRelationshipMaps(site.children, site.id);
        }
      });
    }
    
    // Build the relationship maps
    buildRelationshipMaps(siteTree);
    
    // Function to consolidate parent-child selections
    function consolidateSelections(currentIds) {
      // First: Check if all children of any parent are selected
      // If so, select the parent instead and remove the children
      const parentsToSelect = [];
      const childrenToRemove = [];
      
      Object.keys(parentChildMap).forEach(parentId => {
        if (!currentIds.includes(parentId)) { // Parent not already selected
          const childIds = parentChildMap[parentId];
          if (childIds && childIds.length > 0) {
            // Check if all children are selected
            const allChildrenSelected = childIds.every(id => currentIds.includes(id));
            
            if (allChildrenSelected) {
              parentsToSelect.push(parentId);
              childIds.forEach(id => childrenToRemove.push(id));
            }
          }
        }
      });
      
      // Apply the changes if any were found
      if (parentsToSelect.length > 0 || childrenToRemove.length > 0) {
        const newIds = [...currentIds.filter(id => !childrenToRemove.includes(id)), ...parentsToSelect];
        // Recursively consolidate until no more changes
        return consolidateSelections(newIds);
      }
      
      // Second: Remove children when their parent is selected
      const idsToKeep = [];
      
      currentIds.forEach(id => {
        // Check if this ID is a child of a selected parent
        const parentId = childParentMap[id];
        const isChildOfSelectedParent = parentId && currentIds.includes(parentId);
        
        if (!isChildOfSelectedParent) {
          idsToKeep.push(id);
        }
      });
      
      return idsToKeep;
    }
    
    // Apply the consolidation to the input site IDs
    return consolidateSelections([...siteIds]);
  }
  
  const generateSiteComponents = () => {
    return flattenSiteTree(sites);
  };

  function closeModal() {
    // Reset form state
    setSites([]);
    setSiteIds([]);
   
    // Close the modal programmatically
    const modal = document.getElementById(modalId);
    if (modal) {
      const closeButton = modal.querySelector('.btn-close');
      if (closeButton) {
        closeButton.click();
      }
    }
  }

  // Calculate selected site information
  const selectedSiteNames = selectedSiteIds.map(id => {
    const site = findSiteInTree(sites, id);
    return site ? site.name : '';
  }).filter(name => name !== ''); 

  const selectedSitesCount = selectedSiteNames.length;
  const selectedSitesText = selectedSiteNames.join(', ');

  const selectedSiteCmsIds = selectedSiteIds.map(id => {
    const site = findSiteInTree(sites, id);
    return site ? site.cmsId : null;
  }).filter(cmsId => cmsId !== null);

  return {
    // State
    cms,
    sites,
    selectedSiteIds,
    siteDropdownError,

    setSites,
    setSiteIds,

    setSiteDropdownError,
    
    // Computed values
    selectedSiteNames,
    selectedSitesCount,
    selectedSitesText,
    selectedSiteCmsIds,
    
    // Handlers
    handleSiteClick,
    closeModal,
    
    // Helper functions
    flattenSiteTree,
    getChildrenIds,
    findSiteInTree,
    generateSiteItem,
    generateSiteComponents,
    sortSiteTreeByName
  };
};

export default useModalInitializationForSites;
